/**
 * Registry of currently-mounted Guided Walkthrough targets, keyed by stepId
 * (see `walkthroughStepIds` in constants/featureAnnouncements.js).
 *
 * Module-level, not a Pinia store — mirrors the register/unregister idiom
 * already used by useModalStack.js. A component calls
 * `useWalkthroughTarget(stepId)` to register a real template ref on mount
 * and deregister it on unmount. The Walkthrough Controller calls
 * `getWalkthroughTarget(stepId)` to resolve whichever element is currently
 * registered for the active step — never `document.getElementById`.
 *
 * Bind the returned ref with the STATIC attribute form, matching the variable name:
 *
 *   const savedSearchesButtonTarget = useWalkthroughTarget(walkthroughStepIds.foo);
 *   <AcvButton ref="savedSearchesButtonTarget" />   // ✅ registers
 *   <AcvButton :ref="savedSearchesButtonTarget" />  // ❌ ref stays null
 *
 * The dynamic form does not populate the ref in this Vue/compiler setup, and the failure
 * is silent: nothing registers, so the Controller simply skips that step. Components
 * (not just elements) are fine — the Controller resolves an instance to its element.
 */
const targetRegistry = reactive(new Map());

export const useWalkthroughTarget = (stepId) => {
  const target = ref(null);

  const register = (element) => {
    if (element) targetRegistry.set(stepId, element);
  };

  const unregister = (element) => {
    if (element && targetRegistry.get(stepId) === element) targetRegistry.delete(stepId);
  };

  /**
   * Watching the ref, not just registering on mount, because a target frequently appears
   * *after* its component does: the mobile saved-search list mounts while its fetch is still
   * in flight and only renders its buttons once the data lands. Mount-time registration alone
   * missed those entirely — and silently, since an unregistered step is simply skipped.
   */
  watch(target, (element, previous) => {
    unregister(previous);
    register(element);
  });

  onMounted(() => register(target.value));
  onBeforeUnmount(() => unregister(target.value));

  return target;
};

export const getWalkthroughTarget = (stepId) => computed(() => targetRegistry.get(stepId) ?? null);
